import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Check,
  AlertCircle,
  Link2,
  Unlink,
  Download,
  Upload,
  ChevronDown,
  Loader2,
  CheckCircle2,
  XCircle,
  Users,
  Settings,
  Mail,
  Phone,
  User,
  AlertTriangle,
  X,
} from "lucide-react";
import apiClient from "@/lib/api-client";
import { useAuth } from "@/contexts/AuthContext";

interface GoogleIntegrationStatus {
  connected: boolean;
  googleEmail: string | null;
  autoSyncEnabled: boolean;
  syncDirection: "to_google" | "from_google" | "both";
  lastSyncAt: string | null;
  syncStatus: "connected" | "syncing" | "error" | "disconnected";
  lastError?: string | null;
  stats?: {
    totalCustomers: number;
    syncedCustomers: number;
    pendingSync: number;
  };
}

interface GoogleContact {
  resourceName: string;
  name: string;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  isSynced: boolean;
  canImport: boolean;
  missingFields: string | null;
}

type SyncDirection = "to_google" | "from_google" | "both";

const syncDirectionLabels: Record<SyncDirection, string> = {
  both: "Bidireccional",
  to_google: "Solo a Google",
  from_google: "Solo desde Google",
};

interface GoogleContactsSectionProps {
  onImportComplete?: () => void;
}

export default function GoogleContactsSection({
  onImportComplete,
}: GoogleContactsSectionProps) {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<GoogleIntegrationStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showContacts, setShowContacts] = useState(false);
  const [googleContacts, setGoogleContacts] = useState<GoogleContact[]>([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [isLinking, setIsLinking] = useState(false);
  const [isCleaningOrphans, setIsCleaningOrphans] = useState(false);
  const [contactFilter, setContactFilter] = useState<
    "all" | "synced" | "pending" | "incomplete"
  >("all");

  // Check for URL params (from OAuth callback)
  useEffect(() => {
    if (searchParams.get("google_connected") === "true") {
      setSuccessMessage("¡Google Contacts conectado correctamente!");
      setIsExpanded(true);
      setTimeout(() => setSuccessMessage(null), 5000);
    }
    if (searchParams.get("google_error")) {
      const error = searchParams.get("google_error");
      setErrorMessage(`Error al conectar con Google: ${error}`);
      setIsExpanded(true);
    }
  }, [searchParams]);

  // Fetch status on mount
  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      setIsLoading(true);
      const response = await apiClient.get("/google-contacts/status");
      setStatus(response.data.data);
    } catch (error) {
      console.error("Error fetching Google status:", error);
      setStatus({
        connected: false,
        googleEmail: null,
        autoSyncEnabled: false,
        syncDirection: "both",
        lastSyncAt: null,
        syncStatus: "disconnected",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleConnect = async () => {
    try {
      const response = await apiClient.get("/google-contacts/auth-url");
      const { authUrl } = response.data.data;
      window.location.href = authUrl;
    } catch (error) {
      console.error("Error getting auth URL:", error);
      const axiosError = error as {
        response?: { data?: { message?: string } };
      };
      setErrorMessage(
        axiosError.response?.data?.message || "Error al conectar con Google"
      );
    }
  };

  const handleDisconnect = async () => {
    setIsDisconnecting(true);
    try {
      await apiClient.delete("/google-contacts/disconnect");
      setStatus({
        connected: false,
        googleEmail: null,
        autoSyncEnabled: false,
        syncDirection: "both",
        lastSyncAt: null,
        syncStatus: "disconnected",
      });
      setShowDisconnectConfirm(false);
      setSuccessMessage("Google Contacts desconectado");
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error("Error disconnecting:", error);
      setErrorMessage("Error al desconectar");
    } finally {
      setIsDisconnecting(false);
    }
  };

  const handleSyncToGoogle = async () => {
    try {
      setIsSyncing(true);
      setErrorMessage(null);
      const response = await apiClient.post("/google-contacts/sync-to-google");
      setSuccessMessage(response.data.message);
      setTimeout(() => setSuccessMessage(null), 5000);
      await fetchStatus();
    } catch (error) {
      console.error("Error syncing:", error);
      const axiosError = error as {
        response?: { data?: { message?: string } };
      };
      setErrorMessage(
        axiosError.response?.data?.message || "Error al sincronizar"
      );
    } finally {
      setIsSyncing(false);
    }
  };

  const handleImportFromGoogle = async () => {
    try {
      setIsImporting(true);
      setErrorMessage(null);
      const response = await apiClient.post(
        "/google-contacts/import-from-google"
      );
      setSuccessMessage(response.data.message);
      setTimeout(() => setSuccessMessage(null), 5000);
      await fetchStatus();
      // Notify parent to refresh customers list
      onImportComplete?.();
    } catch (error) {
      console.error("Error importing:", error);
      const axiosError = error as {
        response?: { data?: { message?: string } };
      };
      setErrorMessage(
        axiosError.response?.data?.message || "Error al importar"
      );
    } finally {
      setIsImporting(false);
    }
  };

  const handleLinkExisting = async () => {
    try {
      setIsLinking(true);
      setErrorMessage(null);
      const response = await apiClient.post("/google-contacts/link-existing");
      const { linked, skipped, notFound } = response.data.data;
      setSuccessMessage(
        `Vinculación completada: ${linked} vinculados, ${skipped} ya existentes, ${notFound} sin coincidencia`
      );
      setTimeout(() => setSuccessMessage(null), 5000);
      await fetchStatus();
    } catch (error) {
      console.error("Error linking:", error);
      const axiosError = error as {
        response?: { data?: { message?: string } };
      };
      setErrorMessage(
        axiosError.response?.data?.message || "Error al vincular contactos"
      );
    } finally {
      setIsLinking(false);
    }
  };

  const handleCleanupOrphans = async () => {
    try {
      setIsCleaningOrphans(true);
      setErrorMessage(null);
      const response = await apiClient.post("/google-contacts/cleanup-orphans");
      const { deleted } = response.data.data;
      setSuccessMessage(
        deleted > 0
          ? `Se eliminaron ${deleted} mappings huérfanos`
          : "No se encontraron mappings huérfanos"
      );
      setTimeout(() => setSuccessMessage(null), 5000);
      await fetchStatus();
    } catch (error) {
      console.error("Error cleaning orphans:", error);
      const axiosError = error as {
        response?: { data?: { message?: string } };
      };
      setErrorMessage(
        axiosError.response?.data?.message || "Error al limpiar mappings"
      );
    } finally {
      setIsCleaningOrphans(false);
    }
  };

  const handleUpdateSettings = async (
    settings: Partial<{
      autoSyncEnabled: boolean;
      syncDirection: SyncDirection;
    }>
  ) => {
    try {
      await apiClient.put("/google-contacts/settings", settings);
      setStatus((prev) => (prev ? { ...prev, ...settings } : prev));
      setSuccessMessage("Configuración actualizada");
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error("Error updating settings:", error);
      setErrorMessage("Error al actualizar configuración");
    }
  };

  const handlePreviewGoogleContacts = async () => {
    if (showContacts) {
      setShowContacts(false);
      return;
    }

    try {
      setLoadingContacts(true);
      const response = await apiClient.get("/google-contacts/google-contacts", {
        params: { all: "true" }, // Fetch all contacts
      });
      setGoogleContacts(response.data.data.contacts);
      setShowContacts(true);
    } catch (error) {
      console.error("Error fetching contacts:", error);
      setErrorMessage("Error al cargar contactos de Google");
    } finally {
      setLoadingContacts(false);
    }
  };

  // Check if user can manage (only admin/owner)
  const canManage = user?.role === "admin" || user?.role === "owner";

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex items-center gap-3">
          <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
          <span className="text-gray-500 text-sm">
            Cargando Google Contacts...
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {/* Header - Always visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          {/* Google Icon */}
          <div className="w-10 h-10 bg-white border border-gray-200 rounded-lg flex items-center justify-center">
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
          </div>

          <div className="text-left">
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900">Google Contacts</span>
              {status?.connected ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                  <Check className="w-3 h-3" />
                  Conectado
                </span>
              ) : (
                <span className="inline-flex items-center px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                  No conectado
                </span>
              )}
            </div>
            {status?.connected && status.googleEmail && (
              <p className="text-sm text-gray-500">{status.googleEmail}</p>
            )}
          </div>
        </div>

        <ChevronDown
          className={`w-5 h-5 text-gray-400 transition-transform ${
            isExpanded ? "rotate-180" : ""
          }`}
        />
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-gray-100 p-4 space-y-4">
          {/* Success/Error Messages */}
          {successMessage && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
              <p className="text-green-800 text-sm">{successMessage}</p>
            </div>
          )}

          {errorMessage && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
              <XCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
              <p className="text-red-800 text-sm">{errorMessage}</p>
              <button
                onClick={() => setErrorMessage(null)}
                className="ml-auto text-red-600 hover:text-red-800"
              >
                ×
              </button>
            </div>
          )}

          {status?.connected ? (
            <>
              {/* Stats */}
              {status.stats && (
                <div className="space-y-3">
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                      <p className="text-xl font-bold text-gray-900">
                        {status.stats.totalCustomers}
                      </p>
                      <p className="text-xs text-gray-600">Total</p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-3 text-center">
                      <p className="text-xl font-bold text-green-600">
                        {status.stats.syncedCustomers}
                      </p>
                      <p className="text-xs text-gray-600">Sincronizados</p>
                    </div>
                    <div className="bg-amber-50 rounded-lg p-3 text-center">
                      <p className="text-xl font-bold text-amber-600">
                        {Math.max(0, status.stats.pendingSync)}
                      </p>
                      <p className="text-xs text-gray-600">Pendientes</p>
                    </div>
                  </div>
                  {/* Orphan mappings warning */}
                  {status.stats.pendingSync < 0 && canManage && (
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm text-orange-700">
                            Se detectaron {Math.abs(status.stats.pendingSync)}{" "}
                            mappings huérfanos (clientes eliminados que aún
                            tienen registro de sincronización).
                          </p>
                          <button
                            onClick={handleCleanupOrphans}
                            disabled={isCleaningOrphans}
                            className="mt-2 inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-orange-100 text-orange-800 rounded-md hover:bg-orange-200 transition-colors disabled:opacity-50"
                          >
                            {isCleaningOrphans ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <X className="w-3 h-3" />
                            )}
                            Limpiar mappings huérfanos
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Error message */}
              {status.syncStatus === "error" && status.lastError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-600">{status.lastError}</p>
                </div>
              )}

              {/* Action Buttons */}
              {canManage && (
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={handleSyncToGoogle}
                    disabled={isSyncing || status.syncStatus === "syncing"}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors text-sm disabled:opacity-50"
                  >
                    {isSyncing ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4" />
                    )}
                    <span>Exportar a Google</span>
                  </button>

                  <button
                    onClick={handleImportFromGoogle}
                    disabled={isImporting || status.syncStatus === "syncing"}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 bg-green-50 text-green-700 border border-green-200 rounded-lg hover:bg-green-100 transition-colors text-sm disabled:opacity-50"
                  >
                    {isImporting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4" />
                    )}
                    <span>Importar de Google</span>
                  </button>

                  <button
                    onClick={handleLinkExisting}
                    disabled={isLinking || status.syncStatus === "syncing"}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 bg-purple-50 text-purple-700 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors text-sm disabled:opacity-50"
                  >
                    {isLinking ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Link2 className="w-4 h-4" />
                    )}
                    <span>Vincular existentes</span>
                  </button>

                  <button
                    onClick={() => setShowDisconnectConfirm(true)}
                    className="inline-flex items-center justify-center gap-2 px-3 py-2 text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors text-sm"
                  >
                    <Unlink className="w-4 h-4" />
                    <span className="hidden sm:inline">Desconectar</span>
                  </button>
                </div>
              )}

              {/* View Contacts and Settings Links */}
              <div className="flex flex-wrap gap-4 pt-2 border-t border-gray-100">
                <button
                  onClick={handlePreviewGoogleContacts}
                  disabled={loadingContacts}
                  className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700"
                >
                  {loadingContacts ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Users className="w-4 h-4" />
                  )}
                  {showContacts
                    ? "Ocultar contactos"
                    : "Ver contactos de Google"}
                  <ChevronDown
                    className={`w-4 h-4 transition-transform ${
                      showContacts ? "rotate-180" : ""
                    }`}
                  />
                </button>

                <button
                  onClick={() => setShowSettings(!showSettings)}
                  className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800"
                >
                  <Settings className="w-4 h-4" />
                  Configuración
                  <ChevronDown
                    className={`w-4 h-4 transition-transform ${
                      showSettings ? "rotate-180" : ""
                    }`}
                  />
                </button>
              </div>

              {/* Contacts Table */}
              {showContacts && (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  {/* Filter tabs */}
                  <div className="bg-gray-50 border-b border-gray-200 px-3 py-2 flex flex-wrap gap-2">
                    <button
                      onClick={() => setContactFilter("all")}
                      className={`px-3 py-1 text-xs rounded-full transition-colors ${
                        contactFilter === "all"
                          ? "bg-gray-900 text-white"
                          : "bg-white text-gray-600 border border-gray-300 hover:bg-gray-100"
                      }`}
                    >
                      Todos ({googleContacts.length})
                    </button>
                    <button
                      onClick={() => setContactFilter("synced")}
                      className={`px-3 py-1 text-xs rounded-full transition-colors ${
                        contactFilter === "synced"
                          ? "bg-green-600 text-white"
                          : "bg-white text-gray-600 border border-gray-300 hover:bg-gray-100"
                      }`}
                    >
                      Sincronizados (
                      {googleContacts.filter((c) => c.isSynced).length})
                    </button>
                    <button
                      onClick={() => setContactFilter("pending")}
                      className={`px-3 py-1 text-xs rounded-full transition-colors ${
                        contactFilter === "pending"
                          ? "bg-blue-600 text-white"
                          : "bg-white text-gray-600 border border-gray-300 hover:bg-gray-100"
                      }`}
                    >
                      Pendientes (
                      {
                        googleContacts.filter((c) => !c.isSynced && c.canImport)
                          .length
                      }
                      )
                    </button>
                    <button
                      onClick={() => setContactFilter("incomplete")}
                      className={`px-3 py-1 text-xs rounded-full transition-colors ${
                        contactFilter === "incomplete"
                          ? "bg-gray-500 text-white"
                          : "bg-white text-gray-600 border border-gray-300 hover:bg-gray-100"
                      }`}
                    >
                      Incompletos (
                      {googleContacts.filter((c) => !c.canImport).length})
                    </button>
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {googleContacts.length === 0 ? (
                      <p className="p-4 text-center text-gray-500 text-sm">
                        No se encontraron contactos
                      </p>
                    ) : (
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              Nombre
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">
                              Email
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">
                              Teléfono
                            </th>
                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                              Estado
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {googleContacts
                            .filter((contact) => {
                              if (contactFilter === "all") return true;
                              if (contactFilter === "synced")
                                return contact.isSynced;
                              if (contactFilter === "pending")
                                return !contact.isSynced && contact.canImport;
                              if (contactFilter === "incomplete")
                                return !contact.canImport;
                              return true;
                            })
                            .map((contact) => (
                              <tr
                                key={contact.resourceName}
                                className="hover:bg-gray-50"
                              >
                                <td className="px-3 py-2">
                                  <div className="flex items-center gap-2">
                                    <div className="w-7 h-7 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                                      <User className="w-3.5 h-3.5 text-gray-500" />
                                    </div>
                                    <span className="text-gray-900 truncate max-w-[120px]">
                                      {contact.name}
                                    </span>
                                  </div>
                                </td>
                                <td className="px-3 py-2 hidden sm:table-cell">
                                  {contact.email ? (
                                    <div className="flex items-center gap-1 text-gray-600">
                                      <Mail className="w-3.5 h-3.5" />
                                      <span className="truncate max-w-[150px]">
                                        {contact.email}
                                      </span>
                                    </div>
                                  ) : (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </td>
                                <td className="px-3 py-2 hidden sm:table-cell">
                                  {contact.phone ? (
                                    <div className="flex items-center gap-1 text-gray-600">
                                      <Phone className="w-3.5 h-3.5" />
                                      {contact.phone}
                                    </div>
                                  ) : (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </td>
                                <td className="px-3 py-2 text-center">
                                  {contact.isSynced ? (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                                      <Check className="w-3 h-3" />
                                      Sync
                                    </span>
                                  ) : contact.canImport ? (
                                    <span className="inline-flex items-center px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                                      Pendiente
                                    </span>
                                  ) : (
                                    <span
                                      className="inline-flex items-center px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded-full"
                                      title={
                                        contact.missingFields
                                          ? `Falta: ${contact.missingFields}`
                                          : ""
                                      }
                                    >
                                      Sin {contact.missingFields || "datos"}
                                    </span>
                                  )}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              )}

              {/* Settings Panel */}
              {showSettings && status && (
                <div className="border border-gray-200 rounded-lg p-4 space-y-4 bg-gray-50">
                  {/* Auto-sync toggle */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900 text-sm">
                        Sincronización automática
                      </p>
                      <p className="text-xs text-gray-500">
                        Sincroniza automáticamente al crear/editar clientes
                      </p>
                    </div>
                    <button
                      onClick={() =>
                        handleUpdateSettings({
                          autoSyncEnabled: !status.autoSyncEnabled,
                        })
                      }
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                        status.autoSyncEnabled
                          ? "bg-primary-600"
                          : "bg-gray-200"
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          status.autoSyncEnabled
                            ? "translate-x-5"
                            : "translate-x-0"
                        }`}
                      />
                    </button>
                  </div>

                  {/* Sync direction */}
                  <div>
                    <p className="font-medium text-gray-900 text-sm mb-2">
                      Dirección de sincronización
                    </p>
                    <select
                      value={status.syncDirection}
                      onChange={(e) =>
                        handleUpdateSettings({
                          syncDirection: e.target.value as SyncDirection,
                        })
                      }
                      className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
                    >
                      {Object.entries(syncDirectionLabels).map(
                        ([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        )
                      )}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      {status.syncDirection === "both"
                        ? "Los cambios se sincronizarán en ambas direcciones"
                        : status.syncDirection === "to_google"
                          ? "Solo se enviarán clientes a Google"
                          : "Solo se importarán contactos desde Google"}
                    </p>
                  </div>
                </div>
              )}

              {/* Last sync info */}
              {status.lastSyncAt && (
                <p className="text-xs text-gray-500 text-center">
                  Última sincronización:{" "}
                  {new Date(status.lastSyncAt).toLocaleString("es-ES")}
                </p>
              )}
            </>
          ) : (
            /* Not connected state */
            <div className="text-center py-2">
              <p className="text-gray-600 text-sm mb-4">
                Conecta tu cuenta de Google para sincronizar clientes
                automáticamente
              </p>
              {canManage && (
                <button
                  onClick={handleConnect}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm"
                >
                  <Link2 className="w-4 h-4" />
                  Conectar con Google
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Disconnect Confirmation Modal */}
      {showDisconnectConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowDisconnectConfirm(false)}
          />

          {/* Modal */}
          <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6 animate-in fade-in zoom-in duration-200">
            {/* Close button */}
            <button
              onClick={() => setShowDisconnectConfirm(false)}
              className="absolute top-4 right-4 p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Icon */}
            <div className="flex justify-center mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
            </div>

            {/* Content */}
            <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
              Desconectar Google Contacts
            </h3>
            <p className="text-gray-600 text-center text-sm mb-6">
              ¿Estás seguro de que quieres desconectar Google Contacts? Los
              contactos sincronizados permanecerán en ambas plataformas.
            </p>

            {/* Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => setShowDisconnectConfirm(false)}
                disabled={isDisconnecting}
                className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleDisconnect}
                disabled={isDisconnecting}
                className="flex-1 px-4 py-2.5 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
              >
                {isDisconnecting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Desconectando...
                  </>
                ) : (
                  <>
                    <Unlink className="w-4 h-4" />
                    Desconectar
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
